import * as React from "react";
import { Alert, Box, Card, Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { SimpleBreadcrumbs } from "../../components/navigation/simpleBreadcrumbs";
import StandardLayout from "../../layouts/standard";
import CclGenericAccordian from "../../components/common/cclGenericAccordian";
import AssessmentsInventoryDataGrid from "./assessmentsInventoryDataGrid";
import OrdersInventoryDataGrid from "./ordersInventoryDataGrid";
import { UserClaimsService, eGroupIds, eRoleIds } from "../../services/currentUserService/currentUserService";
import CclTextOrSelectSearchBar, { CclTextOrSelectOption } from "../../components/common/cclLandingPageSearchBars/cclTextOrSelectSearchBar";
import { useGetSharedAdminGroupsQuery, useLazyGetAssessmentAdminGroupByEmailQuery } from "../../services/cclTokenedAssessmentApi";
import { validateEmail } from "../../utilities/generalUtilities";
interface InventoryProps {}

const Inventory: React.FC<InventoryProps> = () => {
    const claimsService = React.useMemo(() => new UserClaimsService(), []);
    const groupKey = claimsService.GetCurrentUserAssesmentAdminGroupId();
    const isClientAdmin = claimsService.IsUserInGroups([eGroupIds.eClientAdmin]);
    const isSuperAdmin = claimsService.IsUserSuperAdmin();
    const isCclAdminAssessmentManager =
        claimsService.IsUserInGroups([eGroupIds.eCCLAdmin]) &&
        claimsService.IsUserInAppRoles([eRoleIds.eAssessmentManager]);
    const showGroupSearch = isSuperAdmin || isCclAdminAssessmentManager;

    const [searchParams] = useSearchParams();
    const initialGroupId = showGroupSearch ? Number(searchParams.get("groupId")) || null : null;
    const initialGroupName = initialGroupId !== null ? searchParams.get("groupName") ?? "" : "";

    const { data: sharedGroups } = useGetSharedAdminGroupsQuery(undefined, { skip: !showGroupSearch });
    const [getGroupByEmail] = useLazyGetAssessmentAdminGroupByEmailQuery();

    const groupOptions: CclTextOrSelectOption[] = React.useMemo(
        () => (sharedGroups ?? []).map((g) => ({ value: g.groupId, label: g.groupName })),
        [sharedGroups]
    );

    const [selectedGroupId, setSelectedGroupId] = React.useState<number | null>(initialGroupId);
    const [selectedGroupName, setSelectedGroupName] = React.useState<string>(initialGroupName);
    const [emailError, setEmailError] = React.useState<string>("");

    const handleSearch = async (textTerm: string, selectValue: string | number) => {
        setSelectedGroupId(null);
        setSelectedGroupName("");

        if (selectValue !== "" && selectValue !== undefined) {
            const id = Number(selectValue);
            const group = (sharedGroups ?? []).find((g) => g.groupId === id);
            setEmailError("");
            setSelectedGroupId(id);
            setSelectedGroupName(group?.groupName ?? "");
            return;
        }

        if (textTerm !== "") {
            if (!validateEmail(textTerm)) {
                setEmailError("Not a valid email address");
                return;
            }
            setEmailError("");
            const result = await getGroupByEmail(textTerm.trim());
            const groups = result.data ?? [];
            if (groups.length === 0) {
                setEmailError("No administrator group found for this email");
                return;
            }
            setSelectedGroupId(groups[0].groupId);
            setSelectedGroupName(groups[0].groupName);
        }
    };

    const handleReset = () => {
        setSelectedGroupId(null);
        setSelectedGroupName("");
        setEmailError("");
    };

    const effectiveGroupKey = showGroupSearch ? (selectedGroupId ?? 0) : groupKey;

    const breadcrumbs = (
        <SimpleBreadcrumbs>
            <Typography variant="h6" color="text.primary">
                {isClientAdmin ? "My Inventory" : "Inventory"}
            </Typography>
            {showGroupSearch && selectedGroupId !== null && selectedGroupName && (
                <Typography variant="h6" color="text.primary">
                    {selectedGroupName}
                </Typography>
            )}
        </SimpleBreadcrumbs>
    );

    return (
        <StandardLayout breadcrumbs={breadcrumbs}>
            <Stack height={1} width={1}>
                <>
                    {showGroupSearch && (
                        <Card sx={{ width: 1, mb: 2 }}>
                            <CclTextOrSelectSearchBar
                                initialTextSearchTerm=""
                                textSearchLabel="Enter Administrator Email"
                                textFieldError={emailError}
                                selectLabel="Select Administrator Group"
                                selectOptions={groupOptions}
                                executeSearch={handleSearch}
                                showResetButton={true}
                                clearSearchTrigger={handleReset}
                            />
                        </Card>
                    )}
                    {isClientAdmin && (
                        <Box
                            sx={{
                                width: "100%",
                                border: "1px solid #E0E0E0",
                                borderRadius: "4px",
                                overflow: "hidden",
                                display: "block",
                                position: "relative",
                            }}
                        >
                            <Box
                                component="img"
                                src="https://coreprodeuscclstg.blob.core.windows.net/public-web-assets/access-assets/images/HomePageTitleBar.jpg"
                                alt="Inventory Title Bar"
                                sx={{
                                    width: "100%",
                                    height: "auto",
                                    display: "block",
                                }}
                            />
                        </Box>
                    )}
                    {(!showGroupSearch || selectedGroupId !== null) && (
                        <Card
                            sx={{
                                width: 1,
                                height: 1,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Stack spacing={2} sx={{ width: "100%", p: 2 }}>
                                <CclGenericAccordian
                                    headingText="Assessments"
                                    defaultExpanded={true}
                                    noGutter={true}
                                >
                                    {effectiveGroupKey !== 0 ? (
                                        <AssessmentsInventoryDataGrid groupKey={effectiveGroupKey} />
                                    ) : (
                                        <Alert severity="info">
                                            No inventory information available.
                                        </Alert>
                                    )}
                                </CclGenericAccordian>

                                <CclGenericAccordian
                                    headingText="Orders"
                                    defaultExpanded={true}
                                    noGutter={true}
                                >
                                    {effectiveGroupKey !== 0 ? (
                                        <OrdersInventoryDataGrid groupKey={effectiveGroupKey} groupName={selectedGroupName || undefined} />
                                    ) : (
                                        <Alert severity="info">No order information available.</Alert>
                                    )}
                                </CclGenericAccordian>
                            </Stack>
                        </Card>
                    )}
                </>
            </Stack>
        </StandardLayout>
    );
};

export default Inventory;
